import { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import type { CatalogRole, IamGrant } from '../../../services/iam';
import { roleTitle, shortRoleKey } from '../iamLookups';
import { ScopeTag } from '../primitives/ScopeTag';

const SOURCE_LABEL: Record<IamGrant['source'], string> = {
    membership: 'Membership',
    assignment: 'Assignment',
    manual: 'Manual',
    impersonation: 'Impersonation'
};

const NO_ID_MESSAGE =
    "Stored, but can't be revoked from here - the API returned no id for this grant.";

interface GrantRowProps {
    grant: IamGrant;
    roleLookup: ReadonlyMap<string, CatalogRole>;
    /** Opens the revoke confirmation; only called for grants that carry an id. Omit to hide revoke UI entirely (read-only viewers). */
    onRevoke?: (grant: IamGrant) => void;
}

function formatDate(iso: string | null): string | null {
    if (!iso) return null;
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString();
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="flex items-center justify-between gap-4 px-3 py-1.5">
            <span className="shrink-0 text-[10px] font-bold text-gray-400 uppercase tracking-[0.04em]">
                {label}
            </span>
            <span className="min-w-0 truncate text-[12px] text-gray-700 text-right">{value}</span>
        </div>
    );
}

// Expandable grant row: collapsed shows role (left) and scope (right);
// expanding reveals key, source, granter, dates and the revoke action.
export function GrantRow({ grant, roleLookup, onRevoke }: GrantRowProps) {
    const [expanded, setExpanded] = useState(false);
    const title = grant.roleKind === 'bundle' ? null : roleTitle(roleLookup.get(grant.roleKey));
    // Every stored grant carries its row id; the guard is purely defensive.
    const canRevoke = Boolean(grant.id) && Boolean(onRevoke);

    return (
        <div className="bg-white">
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                className="w-full flex items-center justify-between gap-3 px-3.5 py-2.5 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 transition-colors"
            >
                <span
                    className={`min-w-0 truncate text-[13px] font-semibold ${
                        title
                            ? 'text-gray-800'
                            : `font-mono ${grant.roleKind === 'bundle' ? 'italic text-violet-700' : 'text-gray-800'}`
                    }`}
                >
                    {title ?? shortRoleKey(grant.roleKey)}
                </span>
                <span className="flex items-center gap-1.5 shrink-0">
                    <ScopeTag
                        scope={grant.scope}
                        label={grant.scope.type === 'platform' ? 'Platform' : grant.scopeLabel}
                        size="sm"
                    />
                    <ChevronDownIcon
                        className={`h-3.5 w-3.5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
                    />
                </span>
            </button>

            {expanded && (
                <div className="px-3.5 pb-3 pt-1 flex flex-col gap-2.5">
                    <div className="rounded-lg border border-gray-200 bg-gray-50/60 divide-y divide-gray-100">
                        <DetailRow
                            label="Role key"
                            value={<span className="font-mono">{grant.roleKey}</span>}
                        />
                        {grant.scopeLabel && <DetailRow label="Scope" value={grant.scopeLabel} />}
                        <DetailRow label="Source" value={SOURCE_LABEL[grant.source]} />
                        <DetailRow label="Granted by" value={grant.grantedBy ?? 'Automatic'} />
                        <DetailRow label="Granted on" value={formatDate(grant.grantedAt) ?? '-'} />
                        <DetailRow label="Expires" value={formatDate(grant.expiresAt) ?? 'Never'} />
                    </div>

                    {onRevoke && (
                        <div className="flex justify-end">
                            {canRevoke ? (
                                <button
                                    type="button"
                                    onClick={() => onRevoke(grant)}
                                    className="bg-red-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300 transition-colors"
                                >
                                    Revoke
                                </button>
                            ) : (
                                <span className="text-[10px] text-gray-400">{NO_ID_MESSAGE}</span>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
